import { useEffect, useState } from 'preact/hooks';
import { useI18n } from '../i18n';
import {
  createMyArticle,
  deleteMyArticle,
  listMyArticles,
  updateMyArticle,
  uploadFile,
  type Article,
  type ArticleDelivery,
  type ArticleKind,
} from '../lib/admin';

const KINDS: ArticleKind[] = ['drink', 'experience', 'ticket', 'gadget', 'other'];
const DELIVERIES: ArticleDelivery[] = ['cantonal', 'national_pickup', 'national', 'digital'];

/// Articles list + add/edit/delete for the authenticated partner. Designed
/// to be embedded inside the PartnerSelf page, not used as a standalone
/// route. Self-contained state — fetches its own data so the parent only
/// has to drop in <PartnerArticles accessToken={t} />. Pass `as` to act
/// against another partner's articles when signed in as admin.
export function PartnerArticles({ accessToken, as }: { accessToken: string; as?: number }) {
  const { t } = useI18n();
  const [list, setList] = useState<Article[] | null>(null);
  const [editing, setEditing] = useState<Article | 'new' | null>(null);
  const [err, setErr] = useState<string | null>(null);

  async function load() {
    setErr(null);
    try {
      const a = await listMyArticles(accessToken, as);
      setList(a);
    } catch (e: any) {
      setErr(e?.message || `${e}`);
    }
  }

  useEffect(() => { load(); }, [accessToken]);

  return (
    <section class="partner-card">
      <header class="partner-card-head">
        <h2 class="partner-card-title">{t('partner.articles.title')}</h2>
        <button class="partner-primary" onClick={() => setEditing('new')}>
          + {t('partner.articles.new')}
        </button>
      </header>
      <p class="partner-muted">{t('partner.articles.lede')}</p>

      {err && <p class="partner-error">{err}</p>}
      {!list && !err && <p class="partner-muted">{t('partner.loading')}</p>}

      {list && list.length === 0 && (
        <p class="partner-muted">{t('partner.articles.none')}</p>
      )}

      {list && list.length > 0 && (
        <div class="partner-table-wrap">
          <table class="partner-table">
            <thead>
              <tr>
                <th />
                <th>{t('partner.articles.th_title')}</th>
                <th>{t('partner.articles.th_kind')}</th>
                <th>{t('partner.articles.th_delivery')}</th>
                <th>{t('partner.articles.th_value')}</th>
                <th>{t('partner.articles.th_stock')}</th>
                <th>{t('partner.articles.th_active')}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {list.map((a) => (
                <tr key={a.id} class={a.active ? '' : 'is-dim'}>
                  <td>
                    {a.imagePath ? (
                      <img src={a.imagePath} alt="" class="partner-thumb" />
                    ) : <span class="partner-dim">—</span>}
                  </td>
                  <td>
                    <div>{a.title}</div>
                    {a.description && (
                      <div class="partner-mono partner-row-sub">{a.description}</div>
                    )}
                  </td>
                  <td>{t(`partner.articles.kind.${a.kind}`)}</td>
                  <td>{t(`partner.articles.delivery_${a.delivery}`)}</td>
                  <td class="partner-mono">{a.valueChf != null ? a.valueChf.toFixed(2) : '—'}</td>
                  <td class="partner-mono">{a.stock != null ? a.stock : '—'}</td>
                  <td>{a.active ? t('partner.articles.yes') : t('partner.articles.no')}</td>
                  <td><button class="partner-link" onClick={() => setEditing(a)}>{t('partner.articles.edit')}</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <ArticleModal
          article={editing === 'new' ? null : editing}
          accessToken={accessToken}
          as={as}
          onClose={() => setEditing(null)}
          onSaved={async () => {
            setEditing(null);
            await load();
          }}
        />
      )}
    </section>
  );
}

function ArticleModal({
  article,
  accessToken,
  as,
  onClose,
  onSaved,
}: {
  article: Article | null;
  accessToken: string;
  as?: number;
  onClose: () => void;
  onSaved: () => void | Promise<void>;
}) {
  const { t } = useI18n();
  const isNew = article === null;
  const [kind, setKind] = useState<ArticleKind>(article?.kind ?? 'other');
  const [title, setTitle] = useState(article?.title ?? '');
  const [description, setDescription] = useState(article?.description ?? '');
  const [imagePath, setImagePath] = useState(article?.imagePath ?? '');
  const [valueChf, setValueChf] = useState(article?.valueChf?.toString() ?? '');
  const [stock, setStock] = useState(article?.stock?.toString() ?? '');
  const [delivery, setDelivery] = useState<ArticleDelivery>(article?.delivery ?? 'cantonal');
  const [active, setActive] = useState(article?.active ?? true);
  const [err, setErr] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  async function onImageFile(e: Event) {
    const input = e.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;
    setErr(null);
    setUploading(true);
    try {
      const res = await uploadFile(accessToken, file, 'articles');
      setImagePath(res.url);
    } catch (e: any) {
      setErr(e?.message || t('partner.venue.upload_failed'));
    } finally {
      setUploading(false);
      input.value = '';
    }
  }

  async function onDelete() {
    if (!article) return;
    if (!confirm(t('partner.articles.soft_delete_confirm').replace('{title}', article.title))) return;
    setSaving(true);
    try {
      await deleteMyArticle(accessToken, article.id, as);
      await onSaved();
    } catch (e: any) {
      setErr(e?.message || t('partner.articles.delete_failed'));
    } finally {
      setSaving(false);
    }
  }

  async function submit(e: Event) {
    e.preventDefault();
    setErr(null);
    setSaving(true);
    try {
      if (!title.trim()) throw new Error(t('partner.articles.title_required'));
      let valueChfNum: number | null = null;
      if (valueChf.trim() !== '') {
        valueChfNum = Number(valueChf);
        if (isNaN(valueChfNum) || valueChfNum < 0) throw new Error(t('partner.articles.value_range'));
      }
      let stockNum: number | null = null;
      if (stock.trim() !== '') {
        stockNum = parseInt(stock, 10);
        if (isNaN(stockNum) || stockNum < 0) throw new Error(t('partner.articles.stock_range'));
      }
      const payload = {
        kind,
        title: title.trim(),
        description: description.trim() || undefined,
        imagePath: imagePath.trim() || undefined,
        valueChf: valueChfNum,
        stock: stockNum,
        delivery,
        active,
      };
      if (isNew) {
        await createMyArticle(accessToken, payload, as);
      } else {
        await updateMyArticle(accessToken, article!.id, payload, as);
      }
      await onSaved();
    } catch (e: any) {
      setErr(e?.message || t('partner.venue.save_failed'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div class="partner-overlay" onClick={onClose}>
      <form class="partner-modal" onClick={(e) => e.stopPropagation()} onSubmit={submit}>
        <h2 class="partner-card-title">
          {isNew ? t('partner.articles.modal_new') : t('partner.articles.modal_edit').replace('{title}', article!.title)}
        </h2>

        <div class="partner-row-2">
          <label class="partner-field">
            <span>{t('partner.articles.title_label')} *</span>
            <input value={title} onInput={(e) => setTitle((e.target as HTMLInputElement).value)} required />
          </label>
          <label class="partner-field">
            <span>{t('partner.articles.kind_label')}</span>
            <select value={kind} onChange={(e) => setKind((e.target as HTMLSelectElement).value as ArticleKind)}>
              {KINDS.map((k) => <option key={k} value={k}>{t(`partner.articles.kind.${k}`)}</option>)}
            </select>
          </label>
        </div>

        <label class="partner-field">
          <span>{t('partner.articles.description')}</span>
          <textarea rows={3} value={description} onInput={(e) => setDescription((e.target as HTMLTextAreaElement).value)} />
        </label>

        <label class="partner-field">
          <span>{t('partner.articles.delivery')}</span>
          <select value={delivery} onChange={(e) => setDelivery((e.target as HTMLSelectElement).value as ArticleDelivery)}>
            {DELIVERIES.map((d) => <option key={d} value={d}>{t(`partner.articles.delivery_${d}`)}</option>)}
          </select>
          <span class="partner-mono" style={{ marginTop: 4 }}>{t('partner.articles.delivery_help')}</span>
        </label>

        <div class="partner-field">
          <span class="partner-field-label">{t('partner.articles.image')}</span>
          <div class="partner-logo-row">
            {imagePath && <img src={imagePath} alt="" class="partner-logo-preview partner-logo-preview--square" />}
            <div class="partner-logo-controls">
              <input
                type="file"
                accept="image/png,image/jpeg,image/webp,image/svg+xml"
                disabled={uploading}
                onChange={onImageFile}
              />
              <input
                value={imagePath}
                onInput={(e) => setImagePath((e.target as HTMLInputElement).value)}
                placeholder="https://…"
                class="partner-mono partner-input-mono"
              />
              {uploading && <p class="partner-muted partner-uploading">{t('partner.venue.uploading')}</p>}
            </div>
          </div>
        </div>

        <div class="partner-row-3">
          <label class="partner-field">
            <span>{t('partner.articles.value')}</span>
            <input inputMode="decimal" value={valueChf} onInput={(e) => setValueChf((e.target as HTMLInputElement).value)} placeholder={t('partner.articles.value_placeholder')} />
          </label>
          <label class="partner-field">
            <span>{t('partner.articles.stock')}</span>
            <input type="number" min={0} value={stock} onInput={(e) => setStock((e.target as HTMLInputElement).value)} placeholder={t('partner.articles.stock_placeholder')} />
          </label>
          <label class="partner-field partner-checkbox">
            <input type="checkbox" checked={active} onChange={(e) => setActive((e.target as HTMLInputElement).checked)} />
            <span>{t('partner.articles.active')}</span>
          </label>
        </div>

        {err && <p class="partner-error">{err}</p>}

        <div class="partner-modal-actions">
          {!isNew && (
            <button type="button" class="partner-link partner-link--danger" onClick={onDelete}>
              {t('partner.articles.soft_delete')}
            </button>
          )}
          <button type="button" class="partner-link" onClick={onClose}>{t('partner.articles.cancel')}</button>
          <button type="submit" class="partner-primary" disabled={saving}>
            {saving ? t('partner.articles.saving') : isNew ? t('partner.articles.create') : t('partner.articles.save')}
          </button>
        </div>
      </form>
    </div>
  );
}
